<template>
  <div>
    <HeroInner descrition="Admin - API Partners" class="mt-70" />

    <div class="grid-box">
      <v-layout bg-white ma-4 mt-5 column br-4 elevation-base>
        <div class="my-donations-header">
          <div>
            <Icon name="fal fa-key title-icon" size="32" colorFont="#C3C6D5" />
            <h2>API Partners</h2>
          </div>
          <div style="display: flex; align-items: center; gap: 16px;">
            <InputText type="search" :showLoader="false" place-holder="Buscar..." v-model="search" />
            <v-btn flat round color="white" class="btn btn-primary px-4" large @click="openCreateDialog">
              <Icon name="fal fa-plus" size="16" class="mr-2" colorFont="#fff" />Novo
            </v-btn>
          </div>
        </div>

        <div>
          <v-data-table
            :headers="headers"
            :items="partners"
            :pagination.sync="pagination"
            :search="search"
            :rows-per-page-items="[25, 50, 100]"
            item-key="id"
            class="text-xs-left"
          >
            <template v-slot:headers="props">
              <tr>
                <th
                  v-for="header in props.headers"
                  :key="header.text"
                  :class="['column sortable', pagination.descending ? 'desc' : 'asc', header.value === pagination.sortBy ? 'active' : '']"
                  @click="changeSort(header.value)"
                >
                  {{ header.text }}
                  <v-icon small>arrow_upward</v-icon>
                </th>
                <th>Ações</th>
              </tr>
            </template>
            <template v-slot:items="props">
              <tr>
                <td>{{ props.item.name }}</td>
                <td>
                  <code>{{ props.item.apiKeyPrefix }}...</code>
                </td>
                <td>
                  <v-chip
                    :color="props.item.isActive ? 'green' : 'red'"
                    text-color="white"
                    small
                  >
                    {{ props.item.isActive ? 'Ativo' : 'Inativo' }}
                  </v-chip>
                </td>
                <td>{{ props.item.contactEmail || '-' }}</td>
                <td>{{ props.item.createdAt | date }}</td>
                <td>
                  <button
                    class="btn-line small mr-2"
                    title="Editar"
                    @click="openEditDialog(props.item)"
                  >
                    <i class="fal fa-pencil"></i>
                  </button>
                  <button
                    class="btn-line small mr-2"
                    :title="props.item.isActive ? 'Inativar' : 'Ativar'"
                    @click="toggleActive(props.item)"
                  >
                    <i :class="props.item.isActive ? 'fal fa-ban' : 'fal fa-power-off'"></i>
                  </button>
                </td>
              </tr>
            </template>
          </v-data-table>
        </div>
      </v-layout>
    </div>

    <!-- Dialog: Criar / Editar partner + webhooks -->
    <v-dialog v-model="showFormDialog" scrollable max-width="700px" persistent>
      <v-card v-if="showFormDialog">
        <v-card-title class="headline">
          <h1>{{ isEditing ? 'Editar API Partner' : 'Nova API Key' }}</h1>
          <v-btn class="close-modal" @click="closeFormDialog">
            <i class="fal fa-times"></i>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text style="max-height: 70vh;">
          <v-layout pa-4 wrap>
            <!-- Dados do partner -->
            <v-flex xs12 class="dialog-field-spacing">
              <v-text-field
                v-model="formPartner.name"
                label="Nome / Descrição *"
                placeholder="Ex: NeoAssist, CRM Interno, Totem Loja"
                :rules="[v => !!v || 'Nome é obrigatório']"
                :disabled="isEditing"
                required
              ></v-text-field>
            </v-flex>
            <v-flex xs12 class="dialog-field-spacing">
              <v-text-field
                v-model="formPartner.contactEmail"
                label="Email de contato (opcional)"
                placeholder="Ex: dev@empresa.com"
                :disabled="isEditing"
              ></v-text-field>
            </v-flex>

            <!-- Empresa (dona das campanhas) — escopo hierárquico (edicao) -->
            <v-flex xs12 class="dialog-field-spacing" v-if="isEditing">
              <v-autocomplete
                class="dialog-ac"
                v-model="formPartner.allowedCompanyId"
                :items="companies"
                item-text="name"
                item-value="id"
                label="Empresa (dona das campanhas)"
                placeholder="Sem empresa (comportamento legado)"
                clearable
                clear-icon="fal fa-times"
                :loading="loadingCompanies"
                no-data-text="Nenhuma empresa encontrada"
                @change="onCompanyChange"
              ></v-autocomplete>
              <div class="caption grey--text mt-1">
                Ao vincular uma empresa, a chave só acessa campanhas dela. Deixe as campanhas
                <b>vazias</b> para liberar <b>todas</b> as campanhas da empresa (novas entram automaticamente).
              </div>
            </v-flex>

            <!-- Campanhas permitidas (dentro da empresa) -->
            <v-flex xs12 class="dialog-field-spacing" v-if="isEditing">
              <v-autocomplete
                class="dialog-ac"
                v-model="formPartner.allowedCampaignIds"
                :items="companyCampaigns"
                item-text="name"
                item-value="id"
                label="Campanhas permitidas"
                :placeholder="formPartner.allowedCompanyId ? 'Todas as campanhas da empresa' : 'Selecione uma empresa primeiro'"
                multiple
                clearable
                clear-icon="fal fa-times"
                :disabled="!formPartner.allowedCompanyId"
                :loading="loadingCampaigns"
                :search-input.sync="campaignSearch"
                no-data-text="Nenhuma campanha encontrada"
                hide-selected
              >
                <template v-slot:selection></template>
              </v-autocomplete>
              <div class="campaign-chips-container" v-if="formPartner.allowedCampaignIds && formPartner.allowedCampaignIds.length">
                <v-chip
                  v-for="cId in formPartner.allowedCampaignIds"
                  :key="cId"
                  close
                  small
                  class="mr-1 mb-1"
                  color="primary"
                  text-color="white"
                  @input="removeCampaign(cId)"
                >{{ getCampaignName(cId) }}</v-chip>
              </div>
              <v-btn
                flat
                round
                color="white"
                class="btn btn-primary px-4 mt-2"
                small
                :loading="savingCampaigns"
                @click="saveCampaigns"
              >
                Salvar escopo (empresa + campanhas)
              </v-btn>
            </v-flex>

            <!-- Permissões (edição) -->
            <v-flex xs12 class="dialog-field-spacing" v-if="isEditing">
              <div class="subtitle-1 mb-2 mt-3" style="font-weight: 500;">Permissões da API</div>
              <div class="permissions-columns">
                <div v-for="section in permissionSections" :key="section.title" class="perm-section">
                  <div class="caption grey--text text-uppercase mb-1" style="font-weight: 600;">{{ section.title }}</div>
                  <div class="permissions-grid">
                    <v-checkbox
                      v-for="perm in section.permissions"
                      :key="perm.value"
                      v-model="formPartner.permissions"
                      :label="perm.label"
                      :value="perm.value"
                      hide-details
                      class="mt-1"
                    ></v-checkbox>
                  </div>
                </div>
              </div>
              <v-btn
                flat
                round
                color="white"
                class="btn btn-primary px-4 mt-2"
                small
                :loading="savingPermissions"
                @click="savePermissions"
              >
                Salvar permissões
              </v-btn>
            </v-flex>

            <!-- Botao salvar (apenas criacao) -->
            <v-flex xs12 class="mt-3" v-if="!isEditing">
              <v-btn
                flat
                round
                color="white"
                class="btn btn-primary px-5"
                large
                :disabled="!formPartner.name"
                :loading="saving"
                @click="createPartner"
              >
                Criar API Key
              </v-btn>
            </v-flex>

            <!-- Chave gerada (exibida uma unica vez apos criacao) -->
            <v-flex xs12 class="mt-4" v-if="generatedKey">
              <v-alert :value="true" type="warning" class="mb-3">
                Esta chave será exibida apenas uma vez. Copie e armazene em local seguro.
              </v-alert>
              <div class="api-key-display">
                <code class="api-key-value">{{ generatedKey }}</code>
              </div>
              <v-btn flat round color="white" class="btn btn-primary px-4 mt-2" small @click="copyToClipboard(generatedKey, 'keyCopied')">
                <Icon name="fal fa-copy" size="14" class="mr-1" colorFont="#fff" />
                {{ keyCopied ? 'Copiado!' : 'Copiar chave' }}
              </v-btn>
            </v-flex>

            <!-- Secao de Webhooks (aparece apos criacao ou em edicao) -->
            <v-flex xs12 class="mt-5" v-if="isEditing">
              <v-divider class="mb-3"></v-divider>
              <div style="display: flex; align-items: center; justify-content: space-between;">
                <h3 style="margin: 0;">Webhooks</h3>
                <v-btn flat round color="white" class="btn btn-primary px-3" small @click="showAddWebhook = true">
                  <Icon name="fal fa-plus" size="14" class="mr-1" colorFont="#fff" />Adicionar
                </v-btn>
              </div>

              <!-- Lista de webhooks existentes -->
              <div v-if="webhooks.length > 0" class="mt-3">
                <div
                  v-for="(wh, index) in webhooks"
                  :key="wh.id"
                  class="webhook-card"
                >
                  <div class="webhook-card-header">
                    <div class="webhook-card-url">
                      <v-chip
                        :color="wh.isActive ? 'green' : 'red'"
                        text-color="white"
                        small
                        class="mr-2"
                      >{{ wh.isActive ? 'Ativo' : 'Inativo' }}</v-chip>
                      <span class="webhook-url-text">{{ wh.url }}</span>
                    </div>
                    <div>
                      <button
                        class="btn-line small mr-1"
                        title="Editar"
                        @click="editWebhook(wh)"
                      >
                        <i class="fal fa-pencil"></i>
                      </button>
                      <button
                        class="btn-line small mr-1"
                        :title="wh.isActive ? 'Inativar' : 'Ativar'"
                        @click="toggleWebhookActive(wh)"
                      >
                        <i :class="wh.isActive ? 'fal fa-ban' : 'fal fa-power-off'"></i>
                      </button>
                      <button
                        class="btn-line small"
                        title="Excluir"
                        @click="deleteWebhook(wh)"
                      >
                        <i class="fal fa-trash-alt"></i>
                      </button>
                    </div>
                  </div>
                  <div class="webhook-card-events">
                    <v-chip
                      v-for="evt in parseEvents(wh.events)"
                      :key="evt"
                      small
                      class="mr-1 mb-1"
                      color="blue-grey lighten-4"
                    >{{ evt }}</v-chip>
                  </div>
                  <div v-if="wh.description" class="webhook-card-desc">{{ wh.description }}</div>
                </div>
              </div>
              <div v-else class="mt-3 text-xs-center" style="color: #999;">
                Nenhum webhook cadastrado.
              </div>

              <!-- Formulario inline para adicionar novo webhook -->
              <div v-if="showAddWebhook" class="webhook-add-form mt-3">
                <v-divider class="mb-3"></v-divider>
                <h4>{{ editingWebhookId ? 'Editar Webhook' : 'Novo Webhook' }}</h4>
                <v-text-field
                  v-model="newWebhook.url"
                  label="URL *"
                  placeholder="https://api.exemplo.com/webhook"
                  :rules="[v => !!v || 'URL obrigatoria', v => (v && v.startsWith('https://')) || 'URL deve iniciar com https://']"
                  required
                ></v-text-field>
                <v-text-field
                  v-model="newWebhook.description"
                  label="Descricao (opcional)"
                  placeholder="Ex: Notificacoes de pagamento"
                ></v-text-field>
                <label class="webhook-events-label">Eventos *</label>
                <div class="webhook-events-checkboxes">
                  <v-checkbox
                    v-for="evt in availableEvents"
                    :key="evt"
                    v-model="newWebhook.events"
                    :label="evt"
                    :value="evt"
                    hide-details
                    class="mt-1"
                  ></v-checkbox>
                </div>
                <div class="mt-3" style="display: flex; gap: 8px;">
                  <v-btn flat round color="white" class="btn btn-default px-4" small @click="cancelAddWebhook">Cancelar</v-btn>
                  <v-btn
                    flat
                    round
                    color="white"
                    class="btn btn-primary px-4"
                    small
                    :disabled="!newWebhook.url || !newWebhook.events.length"
                    :loading="creatingWebhook"
                    @click="editingWebhookId ? updateWebhook() : createWebhook()"
                  >
                    {{ editingWebhookId ? 'Atualizar' : 'Salvar Webhook' }}
                  </v-btn>
                </div>
              </div>
            </v-flex>

            <!-- Secret do webhook recem-criado -->
            <v-flex xs12 class="mt-4" v-if="webhookSecret">
              <v-alert :value="true" type="warning" class="mb-3">
                Este secret sera exibido apenas uma vez. Copie e armazene em local seguro.
                Use-o para validar a assinatura HMAC-SHA256 dos webhooks recebidos.
              </v-alert>
              <div class="api-key-display">
                <code class="api-key-value">{{ webhookSecret }}</code>
              </div>
              <v-btn flat round color="white" class="btn btn-primary px-4 mt-2" small @click="copyToClipboard(webhookSecret, 'secretCopied')">
                <Icon name="fal fa-copy" size="14" class="mr-1" colorFont="#fff" />
                {{ secretCopied ? 'Copiado!' : 'Copiar secret' }}
              </v-btn>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-0">
          <div class="footer-modal-atividades">
            <v-btn flat round color="white" class="btn btn-default px-5" large @click="closeFormDialog">Fechar</v-btn>
          </div>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script type="plain/text">
import HeroInner from "@/components/HeroInner.vue";
import ApiPartnerService from "@/scripts/services/apiPartner.service";
import FormatterHelper from "@/scripts/helpers/formatter.helper";

export default {
  components: {
    HeroInner,
  },
  data: () => ({
    pagination: { sortBy: "name" },
    search: "",
    headers: [
      { text: "Nome", value: "name", align: "left" },
      { text: "Prefixo da Chave", value: "apiKeyPrefix", align: "left" },
      { text: "Status", value: "isActive", align: "center" },
      { text: "Email Contato", value: "contactEmail", align: "left" },
      { text: "Criado em", value: "createdAt", align: "left" },
    ],
    partners: [],
    apiPartnerService: new ApiPartnerService(),
    campaigns: [],
    campaignSearch: null,
    loadingCampaigns: false,
    savingCampaigns: false,
    savingPermissions: false,
    permissionSections: [
      {
        title: "Doações",
        permissions: [
          { value: "donations.list", label: "Listar doações" },
          { value: "donations.read", label: "Consultar status de uma doação" },
          { value: "donations.create", label: "Criar doação (checkout)" },
          { value: "donations.update", label: "Alterar doação (2ª via, valor recorrente)" },
        ],
      },
      {
        title: "Doadores",
        permissions: [
          { value: "donors.list", label: "Listar/consultar doadores" },
        ],
      },
      {
        title: "Campanhas",
        permissions: [
          { value: "campaigns.list", label: "Listar/consultar campanhas" },
          { value: "campaigns.clone", label: "Clonar campanha" },
        ],
      },
      {
        title: "Projetos",
        permissions: [
          { value: "projects.read", label: "Consultar projetos e galeria" },
          { value: "projects.create", label: "Criar projeto" },
          { value: "projects.update", label: "Editar projeto, vínculos e galeria" },
        ],
      },
      {
        title: "Links de pagamento",
        permissions: [
          { value: "payment_links.create", label: "Criar link de pagamento" },
        ],
      },
      {
        title: "Instituições",
        permissions: [
          { value: "institutions.list", label: "Listar/consultar instituições" },
          { value: "institutions.create", label: "Criar instituição" },
          { value: "institutions.image", label: "Upload de imagem" },
        ],
      },
      {
        title: "Empresas",
        permissions: [
          { value: "companies.list", label: "Listar/consultar empresas" },
          { value: "companies.create", label: "Criar empresa" },
          { value: "companies.image", label: "Upload de imagem" },
        ],
      },
      {
        title: "Fundos e Conselhos",
        permissions: [
          { value: "funds.list", label: "Listar fundos e manifestações" },
          { value: "councils.list", label: "Listar/consultar conselhos" },
          { value: "councils.create", label: "Criar conselho" },
        ],
      },
      {
        title: "Dados de referência",
        permissions: [
          { value: "reference.read", label: "Bancos, ODS e áreas de projeto" },
        ],
      },
    ],
    companies: [],
    companyCampaigns: [],
    loadingCompanies: false,
    // Form dialog state
    showFormDialog: false,
    isEditing: false,
    saving: false,
    formPartner: {
      id: null,
      name: "",
      contactEmail: "",
      allowedCompanyId: null,
      allowedCampaignIds: [],
      permissions: [],
    },
    generatedKey: "",
    keyCopied: false,
    // Webhooks
    webhooks: [],
    showAddWebhook: false,
    creatingWebhook: false,
    webhookSecret: "",
    secretCopied: false,
    availableEvents: [
      "donation.created",
      "payment.approved",
      "payment.failed",
      "payment.expired",
      "payment.refunded",
    ],
    editingWebhookId: null,
    newWebhook: {
      url: "",
      description: "",
      events: [],
    },
  }),
  created() {
    this.loadPartners();
  },
  methods: {
    loadPartners() {
      this.apiPartnerService.listAll().then(
        function (data) {
          this.partners = data || [];
        }.bind(this)
      );
    },
    openCreateDialog() {
      this.isEditing = false;
      this.formPartner = { id: null, name: "", contactEmail: "", allowedCompanyId: null, allowedCampaignIds: [], permissions: [] };
      this.generatedKey = "";
      this.keyCopied = false;
      this.webhooks = [];
      this.webhookSecret = "";
      this.secretCopied = false;
      this.showAddWebhook = false;
      this.showFormDialog = true;
    },
    openEditDialog(partner) {
      this.isEditing = true;
      var parsedIds = [];
      try {
        if (partner.allowedCampaignIds) parsedIds = JSON.parse(partner.allowedCampaignIds);
      } catch (e) { parsedIds = []; }
      var parsedPerms = [];
      try {
        if (partner.permissions) parsedPerms = JSON.parse(partner.permissions);
      } catch (e) { parsedPerms = []; }
      this.formPartner = {
        id: partner.id,
        name: partner.name,
        contactEmail: partner.contactEmail,
        allowedCompanyId: partner.allowedCompanyId || null,
        allowedCampaignIds: parsedIds || [],
        permissions: parsedPerms || [],
      };
      this.generatedKey = "";
      this.keyCopied = false;
      this.webhookSecret = "";
      this.secretCopied = false;
      this.showAddWebhook = false;
      this.showFormDialog = true;
      this.loadWebhooks(partner.id);
      this.loadCompanies();
      if (this.formPartner.allowedCompanyId) this.loadCampaignsByCompany(this.formPartner.allowedCompanyId);
    },
    closeFormDialog() {
      this.showFormDialog = false;
      this.formPartner = { id: null, name: "", contactEmail: "", allowedCompanyId: null, allowedCampaignIds: [], permissions: [] };
      this.generatedKey = "";
      this.webhookSecret = "";
      this.webhooks = [];
      this.showAddWebhook = false;
    },
    createPartner() {
      if (!this.formPartner.name) return;
      this.saving = true;

      this.apiPartnerService
        .createWithKey({
          name: this.formPartner.name,
          contactEmail: this.formPartner.contactEmail,
        })
        .then(
          function (data) {
            this.saving = false;
            this.generatedKey = data.rawApiKey;
            this.keyCopied = false;
            this.formPartner.id = data.id;
            this.isEditing = true;
            this.webhooks = [];
            this.loadPartners();
          }.bind(this)
        )
        .catch(
          function () {
            this.saving = false;
          }.bind(this)
        );
    },
    toggleActive(partner) {
      this.apiPartnerService.toggleActive(partner.id).then(
        function (data) {
          partner.isActive = data.isActive;
        }.bind(this)
      );
    },
    // Campaign methods
    loadCompanies() {
      this.loadingCompanies = true;
      this.apiPartnerService.listCompanies().then(
        function (data) {
          this.companies = data || [];
          this.loadingCompanies = false;
        }.bind(this)
      ).catch(
        function () { this.loadingCompanies = false; }.bind(this)
      );
    },
    loadCampaignsByCompany(companyId) {
      if (!companyId) { this.companyCampaigns = []; return; }
      this.loadingCampaigns = true;
      this.apiPartnerService.listCampaignsByCompany(companyId).then(
        function (data) {
          this.companyCampaigns = data || [];
          this.loadingCampaigns = false;
        }.bind(this)
      ).catch(
        function () { this.loadingCampaigns = false; }.bind(this)
      );
    },
    onCompanyChange() {
      // Trocou de empresa: limpa a seleção de campanhas e recarrega as campanhas da empresa.
      this.formPartner.allowedCampaignIds = [];
      this.companyCampaigns = [];
      if (this.formPartner.allowedCompanyId) this.loadCampaignsByCompany(this.formPartner.allowedCompanyId);
    },
    getCampaignName(id) {
      var campaign = this.companyCampaigns.find(function (c) { return c.id === id; });
      return campaign ? campaign.name : id;
    },
    removeCampaign(id) {
      this.formPartner.allowedCampaignIds = this.formPartner.allowedCampaignIds.filter(function (c) { return c !== id; });
    },
    saveCampaigns() {
      this.savingCampaigns = true;
      var allowedIds = this.formPartner.allowedCampaignIds && this.formPartner.allowedCampaignIds.length > 0
        ? JSON.stringify(this.formPartner.allowedCampaignIds)
        : null;

      this.apiPartnerService.updatePartner({
        id: this.formPartner.id,
        allowedCompanyId: this.formPartner.allowedCompanyId || null,
        allowedCampaignIds: allowedIds,
      }).then(
        function () {
          this.savingCampaigns = false;
          this.loadPartners();
        }.bind(this)
      ).catch(
        function () {
          this.savingCampaigns = false;
        }.bind(this)
      );
    },
    savePermissions() {
      this.savingPermissions = true;
      var perms = this.formPartner.permissions && this.formPartner.permissions.length > 0
        ? JSON.stringify(this.formPartner.permissions)
        : null;

      this.apiPartnerService.updatePartner({
        id: this.formPartner.id,
        permissions: perms,
      }).then(
        function () {
          this.savingPermissions = false;
          this.loadPartners();
        }.bind(this)
      ).catch(
        function () {
          this.savingPermissions = false;
        }.bind(this)
      );
    },
    // Webhook methods
    loadWebhooks(apiPartnerId) {
      this.apiPartnerService.listWebhooks(apiPartnerId).then(
        function (data) {
          this.webhooks = data || [];
        }.bind(this)
      );
    },
    parseEvents(eventsJson) {
      try {
        return JSON.parse(eventsJson);
      } catch {
        return [];
      }
    },
    createWebhook() {
      if (!this.newWebhook.url || !this.newWebhook.events.length) return;
      this.creatingWebhook = true;

      this.apiPartnerService.createWebhook({
        apiPartnerId: this.formPartner.id,
        url: this.newWebhook.url,
        description: this.newWebhook.description,
        events: this.newWebhook.events,
      }).then(
        function (data) {
          this.creatingWebhook = false;
          this.showAddWebhook = false;
          this.webhookSecret = data.secret;
          this.secretCopied = false;
          this.newWebhook = { url: "", description: "", events: [] };
          this.loadWebhooks(this.formPartner.id);
        }.bind(this)
      ).catch(
        function () {
          this.creatingWebhook = false;
        }.bind(this)
      );
    },
    cancelAddWebhook() {
      this.showAddWebhook = false;
      this.editingWebhookId = null;
      this.newWebhook = { url: "", description: "", events: [] };
    },
    editWebhook(wh) {
      this.editingWebhookId = wh.id;
      this.newWebhook = {
        url: wh.url,
        description: wh.description || "",
        events: this.parseEvents(wh.events),
      };
      this.showAddWebhook = true;
    },
    updateWebhook() {
      if (!this.newWebhook.url || !this.newWebhook.events.length) return;
      this.creatingWebhook = true;

      this.apiPartnerService.updateWebhook({
        id: this.editingWebhookId,
        url: this.newWebhook.url,
        description: this.newWebhook.description,
        events: this.newWebhook.events,
      }).then(
        function () {
          this.creatingWebhook = false;
          this.showAddWebhook = false;
          this.editingWebhookId = null;
          this.newWebhook = { url: "", description: "", events: [] };
          this.loadWebhooks(this.formPartner.id);
        }.bind(this)
      ).catch(
        function () {
          this.creatingWebhook = false;
        }.bind(this)
      );
    },
    toggleWebhookActive(webhook) {
      this.apiPartnerService.toggleWebhookActive(webhook.id).then(
        function (data) {
          webhook.isActive = data.isActive;
        }.bind(this)
      );
    },
    deleteWebhook(webhook) {
      if (!confirm("Tem certeza que deseja excluir este webhook?")) return;
      this.apiPartnerService.deleteWebhook(webhook.id).then(
        function () {
          this.loadWebhooks(this.formPartner.id);
        }.bind(this)
      );
    },
    copyToClipboard(text, flagName) {
      navigator.clipboard.writeText(text).then(
        function () {
          this[flagName] = true;
          setTimeout(
            function () {
              this[flagName] = false;
            }.bind(this),
            3000
          );
        }.bind(this)
      );
    },
    changeSort(column) {
      if (this.pagination.sortBy === column) {
        this.pagination.descending = !this.pagination.descending;
      } else {
        this.pagination.sortBy = column;
        this.pagination.descending = false;
      }
    },
  },
  filters: {
    date: function (date) {
      return new FormatterHelper().formatDate(date);
    },
  },
};
</script>

<style scoped>
.api-key-display {
  background-color: #fff8e1;
  border: 2px solid #ffc107;
  border-radius: 4px;
  padding: 16px;
  text-align: center;
  margin-top: 8px;
}
.dialog-field-spacing {
  margin-top: 30px !important;
}
.api-key-value {
  font-size: 14px;
  font-family: "Courier New", monospace;
  word-break: break-all;
  color: #333;
  font-weight: bold;
}
.webhook-events-label {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.54);
  display: block;
  margin-bottom: 4px;
}
.webhook-events-checkboxes {
  display: flex;
  flex-direction: column;
}
.webhook-card {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 8px;
  background: #fafafa;
}
.webhook-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.webhook-card-url {
  display: flex;
  align-items: center;
}
.webhook-url-text {
  font-family: "Courier New", monospace;
  font-size: 13px;
  word-break: break-all;
}
.webhook-card-events {
  margin-top: 6px;
}
.webhook-card-desc {
  margin-top: 4px;
  font-size: 12px;
  color: #777;
}
.campaign-chips-container {
  display: flex;
  flex-wrap: wrap;
  margin-top: 4px;
}
/* Autocompletes deste diálogo: manter o "x" (limpar) e a seta DENTRO do campo.
   Neste app o box visível é o próprio <input> (borda via skin global) e o role=combobox
   fica no input (não na raiz), então a regra de box do skin p/ .v-select__slot não casa.
   O input preenche todo o slot; os ícones ficam sobrepostos à direita, dentro da borda. */
.dialog-ac >>> .v-input__slot {
  position: relative;
}
.dialog-ac >>> .v-select__slot input {
  padding-right: 52px;
}
.dialog-ac >>> .v-input__append-inner {
  position: absolute !important;
  right: 12px !important;
  top: 50% !important;
  margin-top: 0 !important;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  background: transparent;
}
/* Permissões em 2 blocos lado a lado; cada seção não quebra entre colunas. */
.permissions-columns {
  column-count: 2;
  column-gap: 32px;
}
.perm-section {
  break-inside: avoid;
  -webkit-column-break-inside: avoid;
  page-break-inside: avoid;
  margin-bottom: 16px;
  display: inline-block;
  width: 100%;
}
@media (max-width: 600px) {
  .permissions-columns {
    column-count: 1;
  }
}
.webhook-add-form {
  background: #f5f5f5;
  border-radius: 6px;
  padding: 16px;
}
.webhook-add-form .v-text-field,
.webhook-add-form .webhook-events-label {
  margin-top: 30px;
}
</style>
